/**
 * Shared resident identity helpers.
 * API always returns room when known; UI opts in via `withRoom`.
 */

const capitalizeWords = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .split(/\s+/)
    .map((part) =>
      part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : "",
    )
    .join(" ");
};

export const getResidentRoom = (resident = {}) => {
  const room =
    resident?.room ??
    resident?.room_no ??
    resident?.room_number ??
    resident?.roomNumber ??
    resident?.care_room_details?.room_no ??
    null;

  if (room === null || room === undefined || room === "") return null;
  return String(room).trim() || null;
};

export const getResidentName = (resident = {}) => {
  if (!resident || typeof resident !== "object") return "";

  const direct =
    resident.name ||
    resident.full_name ||
    resident.names ||
    resident.preferred_name ||
    null;
  if (direct && String(direct).trim()) {
    return String(direct).trim();
  }

  const title =
    resident.title && resident.title !== "''"
      ? capitalizeWords(resident.title)
      : null;
  const parts = [
    title,
    capitalizeWords(resident.first_name),
    capitalizeWords(resident.middle_name),
    capitalizeWords(resident.last_name),
  ].filter(Boolean);

  return parts.join(" ").trim();
};

/**
 * @param {object} resident
 * @param {{ withRoom?: boolean }} [options]
 * @returns {string}
 */
export const formatResidentLabel = (
  resident = {},
  { withRoom = false } = {},
) => {
  const name = getResidentName(resident) || "Unknown resident";
  if (!withRoom) return name;

  const room = getResidentRoom(resident);
  if (!room) return name;

  // Avoid "Room Room11" when room_no already includes a Room prefix.
  const roomLabel = room.replace(/^room[\s-]*/i, "").trim() || room;
  return `${name} (Room ${roomLabel})`;
};
